import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import {
    Banknote,
    CreditCard,
    DollarSign,
    ExternalLink,
    Layers,
    Package,
    QrCode,
    Tag,
    User,
    Wrench,
    X,
    XCircle,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatPhone } from '../../common/formatters';
import { httpClient } from '../../common/httpclient';
import { useEnterpriseConfigContext } from '../context/EnterpriseConfigContext';
import StatusChip from '../common/StatusChip';

// Drawer de preview (somente leitura) da ordem de serviço, aberto a partir da
// linha do Relatório de Ordens de Serviço
// (`pages/reports/ServiceOrderReport.js`). Mesma casca de
// `component/sheet/SaleViewSheet.js` (DESIGN-SYSTEM.md §5.6 — header com
// ícone-chip + título, corpo com cards neutros `background.subtle`, rodapé com
// "Fechar" + "Ver mais") e mesmos endpoints/campos da página cheia
// `pages/service_order/View.js` (`/serviceorders/:id/view`): `/orderservices/:id`,
// `/products`, `/services`, `/values`, `/finance-resume` e `/payments`.
// Checklist e ações (PDF, link, editar) ficam só na página cheia.

function CardHeadRow({ icon: Icon, iconBg, iconFg, title }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: '14px' }}>
            <Box sx={{
                width: 32, height: 32, borderRadius: '9px', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: iconBg, color: iconFg,
            }}>
                <Icon size={16} strokeWidth={2} />
            </Box>
            <Typography sx={{ fontSize: '13.5px', fontWeight: 700, color: 'text.primary' }}>{title}</Typography>
        </Box>
    );
}

function FieldCard({ children, sx }) {
    return (
        <Box sx={{ backgroundColor: 'background.subtle', border: '1px solid', borderColor: 'divider', borderRadius: '12px', p: '16px', mb: '14px', '&:last-child': { mb: 0 }, ...sx }}>
            {children}
        </Box>
    );
}

// Par label/valor read-only — mesmo padrão `RoField` de `SaleViewSheet.js`.
function RoField({ label, value, full = false }) {
    return (
        <Box sx={{ gridColumn: full ? '1 / -1' : 'auto', minWidth: 0 }}>
            <Typography sx={{ fontSize: '10.5px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em', color: 'text.disabled', mb: '4px' }}>
                {label}
            </Typography>
            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: value ? 'text.primary' : 'text.disabled', lineHeight: 1.4, wordBreak: 'break-word' }}>
                {value || '—'}
            </Typography>
        </Box>
    );
}

function FinRow({ icon: Icon, iconColor = 'error.main', label, value, valueColor = 'text.primary', valueWeight = 600, valueSize = '13px' }) {
    return (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: '7px', gap: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                {Icon && (
                    <Box sx={{ color: iconColor, display: 'flex' }}>
                        <Icon size={12} strokeWidth={2} />
                    </Box>
                )}
                <Typography sx={{ fontSize: '12.5px', color: 'text.secondary' }}>{label}</Typography>
            </Box>
            <Typography sx={{ fontSize: valueSize, fontWeight: valueWeight, color: valueColor }}>{value}</Typography>
        </Box>
    );
}

// Mesmas chaves cruas da API que `pages/service_order/View.js` (`PAYMENT_META`).
const PAYMENT_META = {
    'CRÉDITO': { icon: CreditCard, iconBg: 'secondary.bg', iconFg: 'secondary.main', label: 'Cartão de crédito' },
    'DÉBITO': { icon: CreditCard, iconBg: 'info.bg', iconFg: 'primary.main', label: 'Cartão de débito' },
    'PIXKEY': { icon: QrCode, iconBg: 'info.bg', iconFg: 'brand2.main', label: 'PIX - Chave' },
    'PIXQRCODE': { icon: QrCode, iconBg: 'info.bg', iconFg: 'brand2.main', label: 'PIX - QR Code' },
    'DINHEIRO': { icon: Banknote, iconBg: 'success.bg', iconFg: 'success.main', label: 'Dinheiro' },
};

function PaymentRow({ payment }) {
    const meta = PAYMENT_META[payment.method] || { icon: CreditCard, iconBg: 'background.paper', iconFg: 'text.disabled', label: payment.method };
    const Icon = meta.icon;
    let extra = null;
    if (payment.method === 'CRÉDITO' && payment.NumberInstallments > 1) {
        extra = `${payment.NumberInstallments}x de R$ ${payment.ValueInstallment}`;
    } else if (payment.method === 'DINHEIRO' && payment.CashChange && payment.CashChange !== '0,00') {
        extra = `Troco: R$ ${payment.CashChange}`;
    }
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, p: '11px 12px', bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: '10px', mb: '8px', '&:last-child': { mb: 0 } }}>
            <Box sx={{
                width: 30, height: 30, borderRadius: '8px', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: meta.iconBg, color: meta.iconFg,
            }}>
                <Icon size={14} strokeWidth={2} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: '13px', fontWeight: 700, color: 'text.primary' }}>{meta.label}</Typography>
                {extra && (
                    <Typography sx={{ fontSize: '11px', color: 'warning.main', fontWeight: 600, mt: '1px' }}>{extra}</Typography>
                )}
            </Box>
            <Typography sx={{ fontSize: '13.5px', fontWeight: 800, color: 'success.main', flexShrink: 0 }}>
                R$ {payment.amount}
            </Typography>
        </Box>
    );
}

// Mesmo tratamento de data "zero" (`0001-01-01`) de `pages/service_order/View.js`.
function formatOrderDateTime(value) {
    if (!value) return null;
    const formatted = new Date(value).toLocaleString('pt-BR', {
        timeZone: 'UTC',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
    if (formatted === '31/12/1, 23:59' || formatted === '31/12/0001, 23:59') return null;
    return formatted;
}

const EMPTY_ORDER = {
    number: '',
    status: '',
    customer_name: '',
    customer_contact: '',
    vehicle_name: '',
    vehicle_plate: '',
    vehicle_type: '',
    order_date: null,
    delivery_date: null,
    observations: '',
    defect_reason: '',
    cancel_reason: '',
};

const EMPTY_VALUES = {
    total: '0,00',
    totalItems: 0,
    total_products: '0,00',
    total_services: '0,00',
    total_product_item_discount: '0,00',
};

const EMPTY_FINANCE = { subtotal: '0,00', discount: '0,00', total: '0,00', total_liquid: '0,00' };

export default function ServiceOrderViewSheet({ open, onClose, orderId }) {
    const navigate = useNavigate();
    const { settings } = useEnterpriseConfigContext();
    const [loading, setLoading] = useState(false);
    const [order, setOrder] = useState(EMPTY_ORDER);
    const [items, setItems] = useState([]);
    const [values, setValues] = useState(EMPTY_VALUES);
    const [finance, setFinance] = useState(EMPTY_FINANCE);
    const [payments, setPayments] = useState([]);

    useEffect(() => {
        if (!open || !orderId) return;

        const load = async () => {
            setLoading(true);
            try {
                const [orderRes, productsRes, servicesRes, valuesRes, financeRes, paymentsRes] = await Promise.all([
                    httpClient.get(`/orderservices/${orderId}`),
                    httpClient.get(`/orderservices/${orderId}/products`),
                    httpClient.get(`/orderservices/${orderId}/services`),
                    httpClient.get(`/orderservices/${orderId}/values`),
                    httpClient.get(`/orderservices/${orderId}/finance-resume`),
                    httpClient.get(`/orderservices/${orderId}/payments`),
                ]);

                const orderData = orderRes.data?.order || orderRes.data || {};
                setOrder({
                    number: orderData.number || '',
                    status: orderData.status || '',
                    customer_name: orderData.customer_name || '',
                    customer_contact: orderData.customer_contact || '',
                    vehicle_name: orderData.vehicle_name || '',
                    vehicle_plate: orderData.vehicle_plate || '',
                    vehicle_type: orderData.vehicle_type || '',
                    order_date: orderData.order_date || null,
                    delivery_date: orderData.delivery_date || null,
                    observations: orderData.observations || '',
                    defect_reason: orderData.defect_reason || '',
                    cancel_reason: orderData.cancel_reason || '',
                });

                const products = (Array.isArray(productsRes.data) ? productsRes.data : []).map((item) => ({
                    ...item,
                    type: 'product',
                    name: item.name || item.item_name,
                }));
                const services = (Array.isArray(servicesRes.data) ? servicesRes.data : []).map((item) => ({
                    ...item,
                    type: 'service',
                }));
                setItems([...products, ...services]);

                setValues({
                    total: valuesRes.data?.amount ?? '0,00',
                    totalItems: valuesRes.data?.count_items ?? 0,
                    total_products: valuesRes.data?.amount_products ?? '0,00',
                    total_services: valuesRes.data?.amount_services ?? '0,00',
                    total_product_item_discount: valuesRes.data?.total_product_item_discount ?? '0,00',
                });
                setFinance({
                    subtotal: financeRes.data?.subtotal ?? '0,00',
                    discount: financeRes.data?.discount ?? '0,00',
                    total: financeRes.data?.total ?? '0,00',
                    total_liquid: financeRes.data?.total_liquid ?? '0,00',
                });
                setPayments(Array.isArray(paymentsRes.data) ? paymentsRes.data : []);
            } catch (err) {
                console.error('Erro ao carregar ordem de serviço:', err);
                setOrder(EMPTY_ORDER);
                setItems([]);
                setValues(EMPTY_VALUES);
                setFinance(EMPTY_FINANCE);
                setPayments([]);
            } finally {
                setLoading(false);
            }
        };

        load();
    }, [open, orderId]);

    const handleOpenFullPage = () => {
        onClose();
        navigate(`/serviceorders/${orderId}/view`);
    };

    const orderDate = formatOrderDateTime(order.order_date);
    const deliveryDate = formatOrderDateTime(order.delivery_date);
    const isCanceled = order.status === 'Cancelada';
    const hasProductItemDiscount = values.total_product_item_discount && values.total_product_item_discount !== '0,00';
    const showVehicle = settings?.os_use_vehicle && order.vehicle_name;

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    width: { xs: '100%', sm: 480, md: 520 },
                    maxWidth: '100vw',
                    display: 'flex',
                    flexDirection: 'column',
                },
            }}
        >
            {/* drawer-head */}
            <Box sx={{ p: '20px 22px 16px', borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5, flexShrink: 0 }}>
                <Box sx={{ minWidth: 0 }}>
                    <Box sx={{
                        width: 38, height: 38, borderRadius: '11px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        bgcolor: isCanceled ? 'error.bg' : 'secondary.bg', color: isCanceled ? 'error.main' : 'secondary.main', mb: '10px',
                    }}>
                        <Wrench size={18} strokeWidth={2} />
                    </Box>
                    {loading ? (
                        <Skeleton variant="text" width={160} height={28} />
                    ) : (
                        <Typography sx={{ fontSize: '17px', fontWeight: 800, letterSpacing: '-0.01em', mb: '6px' }}>
                            Ordem de serviço #{order.number || '—'}
                        </Typography>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
                        {loading ? (
                            <Skeleton variant="rounded" width={90} height={24} sx={{ borderRadius: '20px' }} />
                        ) : (
                            <StatusChip domain="orderService" value={order.status} />
                        )}
                        {!loading && orderDate && (
                            <Typography sx={{ fontSize: '12.5px', color: 'text.secondary' }}>{orderDate}</Typography>
                        )}
                    </Box>
                </Box>
                <IconButton onClick={onClose} size="small" aria-label="Fechar">
                    <X size={18} strokeWidth={2} />
                </IconButton>
            </Box>

            {/* drawer-body */}
            <Box sx={{ p: '20px 22px', flex: 1, overflowY: 'auto' }}>
                {loading ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                        <Skeleton variant="rounded" height={130} sx={{ borderRadius: '12px' }} />
                        <Skeleton variant="rounded" height={160} sx={{ borderRadius: '12px' }} />
                        <Skeleton variant="rounded" height={140} sx={{ borderRadius: '12px' }} />
                        <Skeleton variant="rounded" height={90} sx={{ borderRadius: '12px' }} />
                    </Box>
                ) : (
                    <>
                        {isCanceled && order.cancel_reason && (
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '10px', p: '12px 14px', borderRadius: '12px', mb: '14px', bgcolor: 'error.bg', border: '1px solid', borderColor: 'error.main' }}>
                                <Box sx={{ color: 'error.main', display: 'flex', flexShrink: 0, mt: '2px' }}>
                                    <XCircle size={16} strokeWidth={2} />
                                </Box>
                                <Box>
                                    <Typography sx={{ fontSize: '10.5px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.04em', color: 'error.main', mb: '2px' }}>
                                        Motivo do cancelamento
                                    </Typography>
                                    <Typography sx={{ fontSize: '13px', fontWeight: 600, color: 'error.main' }}>
                                        {order.cancel_reason}
                                    </Typography>
                                </Box>
                            </Box>
                        )}

                        {/* Informações */}
                        <FieldCard>
                            <CardHeadRow icon={User} iconBg="secondary.bg" iconFg="secondary.main" title="Informações" />
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 16px' }}>
                                <RoField label="Data do pedido" value={orderDate} />
                                <RoField label="Data de entrega" value={deliveryDate} />
                                <RoField label="Cliente" value={order.customer_name} />
                                <RoField label="Contato" value={order.customer_contact ? formatPhone(order.customer_contact) : ''} />
                                {showVehicle && (
                                    <RoField
                                        full
                                        label="Veículo"
                                        value={
                                            order.vehicle_plate && order.vehicle_type !== 'bicycle'
                                                ? `${order.vehicle_name} · Placa ${order.vehicle_plate}`
                                                : order.vehicle_name
                                        }
                                    />
                                )}
                                {order.defect_reason && <RoField full label="Motivo/Defeito" value={order.defect_reason} />}
                                {order.observations && <RoField full label="Observações" value={order.observations} />}
                            </Box>
                        </FieldCard>

                        {/* Itens da ordem */}
                        <FieldCard>
                            <CardHeadRow icon={Layers} iconBg="info.bg" iconFg="primary.main" title={`Itens da ordem (${values.totalItems})`} />
                            {items.length === 0 ? (
                                <Typography sx={{ fontSize: '12.5px', color: 'text.disabled', textAlign: 'center', py: 1.5 }}>
                                    Esta ordem não possui itens registrados
                                </Typography>
                            ) : (
                                <Box sx={{ maxHeight: 280, overflowY: 'auto' }}>
                                    {items.map((item, idx) => {
                                        const isService = item.type === 'service';
                                        const Icon = isService ? Wrench : Package;
                                        return (
                                            <Box
                                                key={item.id || idx}
                                                sx={{
                                                    display: 'flex', alignItems: 'center', gap: '12px',
                                                    p: '9px 10px', borderRadius: '10px', mb: '8px', '&:last-child': { mb: 0 },
                                                    bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider',
                                                }}
                                            >
                                                <Box sx={{
                                                    width: 38, height: 38, borderRadius: '8px', flexShrink: 0,
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    bgcolor: isService ? 'secondary.bg' : 'info.bg',
                                                    color: isService ? 'secondary.main' : 'primary.main',
                                                }}>
                                                    <Icon size={17} strokeWidth={1.8} />
                                                </Box>
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Typography sx={{ fontSize: '13px', fontWeight: 700, color: 'text.primary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                        {item.name}
                                                    </Typography>
                                                    <Typography sx={{ fontSize: '11px', color: 'text.disabled', mt: '1px' }}>
                                                        {`${item.quantity} × R$ ${item.amount_unit}`}
                                                        {!isService && item.item_discount_amount ? ` · Desc. −R$ ${item.item_discount_amount}` : ''}
                                                    </Typography>
                                                </Box>
                                                <Typography sx={{ fontSize: '13px', fontWeight: 800, color: 'text.primary', flexShrink: 0 }}>
                                                    R$ {item.amount_total}
                                                </Typography>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            )}
                        </FieldCard>

                        {/* Resumo financeiro */}
                        <FieldCard>
                            <CardHeadRow icon={DollarSign} iconBg="success.bg" iconFg="success.main" title="Resumo financeiro" />
                            <FinRow label="Produtos" value={`R$ ${values.total_products}`} />
                            {hasProductItemDiscount && (
                                <FinRow label="Desc. produtos" value={`− R$ ${values.total_product_item_discount}`} valueColor="error.main" />
                            )}
                            <FinRow label="Serviços" value={`R$ ${values.total_services}`} />
                            <Divider sx={{ my: '2px' }} />
                            <FinRow label="Subtotal" value={`R$ ${finance.subtotal}`} />
                            <FinRow icon={Tag} label="Desconto" value={`− R$ ${finance.discount}`} valueColor="error.main" />
                            <FinRow label="Total" value={`R$ ${finance.total}`} valueColor="success.main" valueWeight={800} valueSize="15px" />
                            <FinRow label="Total líquido" value={`R$ ${finance.total_liquid}`} valueWeight={700} />
                        </FieldCard>

                        {/* Pagamentos */}
                        <FieldCard>
                            <CardHeadRow icon={CreditCard} iconBg="warning.bg" iconFg="warning.main" title="Pagamentos" />
                            {payments.length === 0 ? (
                                <Typography sx={{ fontSize: '12.5px', color: 'text.disabled', textAlign: 'center', py: 1.5 }}>
                                    Nenhum pagamento registrado para esta ordem
                                </Typography>
                            ) : (
                                payments.map((payment, idx) => (
                                    <PaymentRow key={idx} payment={payment} />
                                ))
                            )}
                        </FieldCard>
                    </>
                )}
            </Box>

            {/* drawer-foot */}
            <Box sx={{ p: '16px 22px 20px', borderTop: '1px solid', borderColor: 'divider', display: 'flex', gap: '10px', justifyContent: 'flex-end', flexShrink: 0 }}>
                <Button variant="outlined" onClick={onClose}>Fechar</Button>
                <Button variant="contained" endIcon={<ExternalLink size={15} strokeWidth={2} />} onClick={handleOpenFullPage}>
                    Ver mais
                </Button>
            </Box>
        </Drawer>
    );
}
